import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { RootState, AppDispatch } from '@/store'
// @ts-ignore - Import issues with selectors
import {
    fetchConversations,
    fetchMessages,
    sendMessage,
    markMessagesAsRead,
    deleteMessage,
    clearError,
    setCurrentConversation
} from '@/store/slices/messagesSlice'
import { socketService } from '@/services/socketService'
import {
    FiMessageSquare,
    FiUser,
    FiSend,
    FiMoreVertical,
    FiSearch,
    FiCheck,
    FiCheckCircle,
    FiTrash2,
    FiArchive,
    FiVolumeX,
    FiFlag,
    FiArrowLeft
} from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Avatar from '@/components/ui/Avatar'
import { formatTimeAgo } from '@/utils/formatters'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
// @ts-ignore - Unused import Message
import { Message, Conversation } from '@/types/message'
import { User } from '@/types/auth'

const MessagesPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const { conversationId: urlConversationId } = useParams<{ conversationId?: string }>()
    const { showSuccess, showError, showInfo } = useSnackbarContext()
    const { user } = useSelector((state: RootState) => state.auth)
    // @ts-ignore - Selector function doesn't exist, using direct state access
    const conversations = useSelector((state: RootState) => state.messages.conversations)
    // @ts-ignore - Selector function doesn't exist, using direct state access
    const messages = useSelector((state: RootState) => state.messages.messages)
    // @ts-ignore - Selector function doesn't exist, using direct state access
    const loading = useSelector((state: RootState) => state.messages.isLoading)
    // @ts-ignore - Selector function doesn't exist, using direct state access
    const typingUsers = useSelector((state: RootState) => state.messages.typingUsers)
    // @ts-ignore - Selector function doesn't exist, using direct state access
    const isConnected = useSelector((state: RootState) => state.messages.isConnected)

    const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
    const [sending, setSending] = useState(false)
    const [newMessage, setNewMessage] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null)
    const [showKebabMenu, setShowKebabMenu] = useState(false)
    const [isTyping, setIsTyping] = useState(false)
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null)
    const [previousMessagesLength, setPreviousMessagesLength] = useState(0)
    const [isMobile, setIsMobile] = useState(false)
    const [showConversationList, setShowConversationList] = useState(true)

    const kebabMenuRef = useRef<HTMLDivElement>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const messagesContainerRef = useRef<HTMLDivElement>(null)

    // Handle responsive design and screen size changes
    useEffect(() => {
        const checkScreenSize = () => {
            const isMobileScreen = window.innerWidth < 768
            setIsMobile(isMobileScreen)

            // On mobile, when a conversation is selected, hide the conversation list
            if (isMobileScreen && selectedConversationId) {
                setShowConversationList(false)
            } else if (!isMobileScreen) {
                // On desktop, always show conversation list
                setShowConversationList(true)
            }
        }

        // Check on component mount
        checkScreenSize()

        // Add event listener for window resize
        window.addEventListener('resize', checkScreenSize)

        // Cleanup event listener
        return () => {
            window.removeEventListener('resize', checkScreenSize)
        }
    }, [selectedConversationId])

    // Fetch conversations on component mount
    useEffect(() => {
        if (user) {
            // @ts-ignore - Parameter mismatch
            dispatch(fetchConversations({}))
        }
    }, [dispatch, user])

    // Select conversation from URL parameter or first conversation if none selected
    useEffect(() => {
        // @ts-ignore - Type unknown array access
        if (conversations.length > 0) {
            if (urlConversationId) {
                // Check if the URL conversation ID exists in the conversations list
                // @ts-ignore - Type unknown array access
                const conversationExists = conversations.some(conv => conv._id === urlConversationId)
                if (conversationExists) {
                    setSelectedConversationId(urlConversationId)
                } else {
                    // If URL conversation doesn't exist, select first conversation
                    // @ts-ignore - Type unknown array access
                    setSelectedConversationId(conversations[0]._id)
                }
            } else if (!selectedConversationId) {
                // @ts-ignore - Type unknown array access
                setSelectedConversationId(conversations[0]._id)
            }
        }
    }, [conversations, urlConversationId, selectedConversationId])

    // Fetch messages for selected conversation and set current conversation
    useEffect(() => {
        if (selectedConversationId) {
            // @ts-ignore - Parameter type mismatch
            dispatch(fetchMessages({ conversationId: selectedConversationId }))

            // Set current conversation for socket message handling
            // @ts-ignore - Type unknown array access
            const conversation = conversations.find(conv => conv._id === selectedConversationId)
            if (conversation) {
                dispatch(setCurrentConversation(conversation))
            }
        }
    }, [dispatch, selectedConversationId, conversations])

    // Mark messages as read when conversation is selected
    useEffect(() => {
        // @ts-ignore - Type unknown array access
        if (selectedConversationId && messages.length > 0) {
            // @ts-ignore - Type unknown array access
            const unreadMessages = messages.filter((msg: any) => {
                const senderId = typeof msg.sender === 'string' ? msg.sender : msg.sender?._id
                return !msg.read && senderId !== user?._id
            })
            if (unreadMessages.length > 0) {
                dispatch(markMessagesAsRead(selectedConversationId))
            }
        }
    }, [dispatch, selectedConversationId, messages, user])

    // Handle click outside kebab menu
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (kebabMenuRef.current && !kebabMenuRef.current.contains(event.target as Node)) {
                setShowKebabMenu(false)
            }
        }

        if (showKebabMenu) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showKebabMenu])

    // Join/leave socket rooms when conversation changes
    useEffect(() => {
        if (selectedConversationId && socketService.isConnected()) {
            socketService.joinConversation(selectedConversationId)
        }

        return () => {
            if (selectedConversationId && socketService.isConnected()) {
                socketService.leaveConversation(selectedConversationId)
            }
        }
    }, [selectedConversationId])

    // Check if user is near the bottom of the chat
    const isNearBottom = useCallback(() => {
        const container = messagesContainerRef.current
        if (!container) return true

        const threshold = 100 // pixels from bottom
        const { scrollTop, scrollHeight, clientHeight } = container
        return scrollHeight - scrollTop - clientHeight < threshold
    }, [])

    // Manual scroll to bottom function
    const scrollToBottom = useCallback(() => {
        const container = messagesContainerRef.current
        if (container) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth'
            })
        }
    }, [])

    // Auto-scroll to bottom when new messages arrive (only if user is near bottom or it's a new message)
    useEffect(() => {
        // @ts-ignore - Type unknown array access
        const currentLength = messages.length

        // If this is the initial load (messages just fetched), don't auto-scroll
        if (previousMessagesLength === 0 && currentLength > 0) {
            setPreviousMessagesLength(currentLength)
            return
        }

        // If new messages were added and user is near the bottom, scroll to bottom
        if (currentLength > previousMessagesLength && isNearBottom()) {
            scrollToBottom()
        }

        setPreviousMessagesLength(currentLength)
    }, [messages, previousMessagesLength, isNearBottom, scrollToBottom])

    // Clear error when component mounts
    useEffect(() => {
        dispatch(clearError())
    }, [dispatch])

    // Cleanup typing timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeout) {
                clearTimeout(typingTimeout)
            }
        }
    }, [typingTimeout])

    // Handle typing functionality
    const handleTyping = useCallback((value: string) => {
        setNewMessage(value)

        if (!selectedConversationId || !socketService.isConnected()) return

        if (value.trim() && !isTyping) {
            setIsTyping(true)
            socketService.startTyping(selectedConversationId)
        }

        // Clear existing timeout
        if (typingTimeout) {
            clearTimeout(typingTimeout)
        }

        // Set new timeout to stop typing after 3 seconds of inactivity
        const timeout = setTimeout(() => {
            if (isTyping && selectedConversationId && socketService.isConnected()) {
                setIsTyping(false)
                socketService.stopTyping(selectedConversationId)
            }
        }, 3000)

        setTypingTimeout(timeout)
    }, [selectedConversationId, isTyping, typingTimeout])

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!newMessage.trim() || !selectedConversationId || !user) return

        // Stop typing indicator when sending message
        if (isTyping && socketService.isConnected()) {
            setIsTyping(false)
            socketService.stopTyping(selectedConversationId)
            if (typingTimeout) {
                clearTimeout(typingTimeout)
                setTypingTimeout(null)
            }
        }

        setSending(true)

        try {
            const result = await dispatch(sendMessage({
                conversationId: selectedConversationId,
                content: newMessage.trim(),
                type: 'text'
            }))

            if (sendMessage.fulfilled.match(result)) {
                setNewMessage('')
                showSuccess('Message sent successfully')
                // Scroll to bottom when sending a new message
                scrollToBottom()
            }
        } catch (error) {
            showError('Failed to send message')
            console.error('Failed to send message:', error)
        } finally {
            setSending(false)
        }
    }

    const handleConversationSelect = (conversationId: string) => {
        setSelectedConversationId(conversationId)

        // On mobile, hide conversation list when a conversation is selected
        if (isMobile) {
            setShowConversationList(false)
        }
    }

    const handleBackToConversations = () => {
        if (isMobile) {
            setShowConversationList(true)
            // Optionally clear selection on mobile
            // setSelectedConversationId(null)
        }
    }

    const handleDeleteMessage = async (messageId: string) => {
        if (!user) return

        setDeletingMessageId(messageId)

        try {
            const result = await dispatch(deleteMessage(messageId))

            if (deleteMessage.fulfilled.match(result)) {
                showSuccess('Message deleted successfully')
            }
        } catch (error) {
            showError('Failed to delete message')
            console.error('Failed to delete message:', error)
        } finally {
            setDeletingMessageId(null)
        }
    }

    const handleKebabMenuToggle = () => {
        setShowKebabMenu(!showKebabMenu)
    }

    const handleArchiveConversation = () => {
        if (selectedConversationId) {
            // TODO: Implement archive functionality when ready
            showInfo('Archive functionality will be available soon')
            setShowKebabMenu(false)
        }
    }

    const handleMuteConversation = () => {
        if (selectedConversationId) {
            // TODO: Implement mute functionality when ready
            showInfo('Mute functionality will be available soon')
            setShowKebabMenu(false)
        }
    }

    const handleReportConversation = () => {
        if (selectedConversationId) {
            // TODO: Implement report functionality when ready
            showInfo('Report functionality will be available soon')
            setShowKebabMenu(false)
        }
    }

    const selectedConversation = conversations.find(conv => conv._id === selectedConversationId)

    const getOtherParticipant = useCallback((conversation: Conversation): User | undefined => {
        if (!conversation.participants || !Array.isArray(conversation.participants)) {
            // Only log warning once per conversation to avoid excessive logging
            if (!conversation._loggedParticipantWarning) {
                console.warn('Conversation participants is undefined or not an array:', conversation._id, conversation.participants)
                // @ts-ignore - Add flag to avoid repeated warnings
                conversation._loggedParticipantWarning = true
            }
            return undefined
        }
        return conversation.participants.find(p => p._id !== user?._id)
    }, [user?._id])

    const filteredConversations = useMemo(() => {
        return conversations.filter(conv => {
            if (!searchQuery.trim()) return true
            const otherParticipant = getOtherParticipant(conv)
            const firstName = otherParticipant?.profile?.firstName ?? ''
            const lastName = otherParticipant?.profile?.lastName ?? ''
            const participantName = `${firstName} ${lastName}`.trim()
            return participantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                conv.metadata?.gigTitle?.toLowerCase().includes(searchQuery.toLowerCase())
        })
    }, [conversations, searchQuery, getOtherParticipant])

    // Memoize the selected conversation's other participant to avoid repeated calculations
    const selectedOtherParticipant = useMemo(() => {
        return selectedConversation ? getOtherParticipant(selectedConversation) : undefined
    }, [selectedConversation, getOtherParticipant])

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    return (
        <>
            <Helmet>
                <title>Messages - SideGO</title>
                <meta name="description" content="Communicate with clients and workers through our messaging system" />
            </Helmet>

            <div className={`max-w-7xl mx-auto ${isMobile ? 'px-0 py-0' : 'px-4 sm:px-6 lg:px-8 py-6 sm:py-8'}`}>
                {!isMobile && (
                    <div className="mb-6 sm:mb-8">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                            Messages
                        </h1>
                        <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                            Communicate with clients and manage your conversations
                        </p>
                    </div>
                )}

                <div
                    className={`bg-white dark:bg-gray-800 ${isMobile ? '' : 'rounded-lg shadow-sm'} overflow-hidden`}
                    style={{ height: isMobile ? 'calc(100vh - 64px)' : '600px' }}
                >
                    <div className="flex h-full relative">
                        {/* Conversations Sidebar - Responsive */}
                        <div className={`${isMobile
                            ? `absolute inset-0 z-10 ${showConversationList ? 'block' : 'hidden'}`
                            : 'w-80 border-r border-gray-200 dark:border-gray-700'
                            } flex flex-col bg-white dark:bg-gray-800`}>
                            {/* Mobile Header - Only show on mobile */}
                            {isMobile && (
                                <div className="p-4 sm:p-5 md:p-6 border-b border-gray-200 dark:border-gray-700">
                                    <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                                        Messages
                                    </h2>
                                </div>
                            )}

                            {/* Search */}
                            <div className="p-4 sm:p-5 md:p-6 border-b border-gray-200 dark:border-gray-700">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiSearch className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-xs sm:text-sm md:text-base"
                                        placeholder="Search conversations..."
                                    />
                                </div>
                            </div>

                            {/* Conversations List */}
                            <div className="flex-1 overflow-y-auto">
                                {filteredConversations.length > 0 ? (
                                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {filteredConversations.map((conversation) => {
                                            const otherParticipant = getOtherParticipant(conversation)
                                            const isActive = selectedConversation?._id === conversation._id

                                            return (
                                                <div
                                                    key={conversation._id}
                                                    onClick={() => handleConversationSelect(conversation._id)}
                                                    className={`${isMobile ? 'p-4 min-h-[76px]' : 'p-4'
                                                        } cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors active:bg-gray-100 dark:active:bg-gray-600 ${isActive ? 'bg-primary-50 dark:bg-primary-900/20 border-r-2 border-primary-500' : ''
                                                        }`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className="relative flex-shrink-0">
                                                            <Avatar
                                                                avatarId={otherParticipant?.profile?.avatar}
                                                                alt={`${otherParticipant?.profile?.firstName} ${otherParticipant?.profile?.lastName}`}
                                                                size="md"
                                                                className="w-12 h-12 bg-gray-100 dark:bg-gray-700"
                                                                fallbackIcon={<FiUser className="w-6 h-6 text-gray-500" />}
                                                            />
                                                        </div>

                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-1 gap-2">
                                                                <h3 className={`text-xs sm:text-sm md:text-base font-semibold truncate ${
                                                                    (otherParticipant as any)?.isDeleted
                                                                        ? 'text-gray-400 dark:text-gray-500 italic'
                                                                        : 'text-gray-900 dark:text-white'
                                                                }`}>
                                                                    {(otherParticipant as any)?.isDeleted
                                                                        ? 'Deleted Account'
                                                                        : otherParticipant?.profile?.firstName && otherParticipant?.profile?.lastName
                                                                            ? `${otherParticipant.profile.firstName} ${otherParticipant.profile.lastName}`
                                                                            : otherParticipant?.profile?.firstName || 'Unknown User'
                                                                    }
                                                                </h3>
                                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                                    {/* @ts-ignore - unreadCount possibly undefined */}
                                                                    {conversation.unreadCount > 0 && (
                                                                        <span className="bg-primary-600 text-white text-xs sm:text-xs font-medium px-2 py-1 rounded-full">
                                                                            {conversation.unreadCount}
                                                                        </span>
                                                                    )}
                                                                    <span className="text-xs sm:text-xs text-gray-500 dark:text-gray-400">
                                                                        {formatTimeAgo(conversation.updatedAt)}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {conversation.metadata?.gigTitle && (
                                                                <p className="text-xs sm:text-xs text-primary-600 dark:text-primary-400 mb-1 truncate">
                                                                    Re: {conversation.metadata.gigTitle}
                                                                </p>
                                                            )}

                                                            <p className={`text-xs sm:text-sm md:text-base truncate ${
                                                                (conversation.lastMessage as any)?.sender?.isDeleted
                                                                    ? 'text-gray-400 dark:text-gray-500 italic'
                                                                    : 'text-gray-600 dark:text-gray-400'
                                                            }`}>
                                                                {conversation.lastMessage && (
                                                                    <>
                                                                        {(typeof conversation.lastMessage.sender === 'string'
                                                                            ? conversation.lastMessage.sender === user?._id
                                                                            : conversation.lastMessage.sender && conversation.lastMessage.sender._id === user?._id) ? 'You: ' : ''}
                                                                        {(conversation.lastMessage as any)?.sender?.isDeleted ? '[Deleted Account]' : conversation.lastMessage.content}
                                                                    </>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-center p-4 sm:p-5 md:p-6">
                                        <div>
                                            <FiMessageSquare className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-gray-300 dark:text-gray-600 mx-auto mb-3 sm:mb-4" />
                                            <p className="text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-400">
                                                {searchQuery ? 'No conversations found' : 'No conversations yet'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Chat Area - Responsive */}
                        <div className={`${isMobile
                            ? `absolute inset-0 z-20 ${showConversationList ? 'hidden' : 'block'}`
                            : 'flex-1'
                            } flex flex-col bg-white dark:bg-gray-800`}>
                            {selectedConversation ? (
                                <>
                                    {/* Chat Header */}
                                    <div className="p-4 sm:p-5 md:p-6 border-b border-gray-200 dark:border-gray-700">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                                {/* Back button for mobile */}
                                                {isMobile && (
                                                    <button
                                                        onClick={handleBackToConversations}
                                                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex-shrink-0"
                                                    >
                                                        <FiArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </button>
                                                )}

                                                <div className="relative flex-shrink-0">
                                                    <Avatar
                                                        avatarId={selectedOtherParticipant?.profile?.avatar}
                                                        alt={`${selectedOtherParticipant?.profile?.firstName} ${selectedOtherParticipant?.profile?.lastName}`}
                                                        size="sm"
                                                        className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 dark:bg-gray-700"
                                                        fallbackIcon={<FiUser className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />}
                                                    />
                                                </div>

                                                <div className="min-w-0 flex-1">
                                                    <h2 className={`text-xs sm:text-sm md:text-base font-semibold truncate ${
                                                        (selectedOtherParticipant as any)?.isDeleted
                                                            ? 'text-gray-400 dark:text-gray-500 italic'
                                                            : 'text-gray-900 dark:text-white'
                                                    }`}>
                                                        {(selectedOtherParticipant as any)?.isDeleted 
                                                            ? 'Deleted Account' 
                                                            : selectedOtherParticipant?.profile?.firstName && selectedOtherParticipant?.profile?.lastName
                                                                ? `${selectedOtherParticipant.profile.firstName} ${selectedOtherParticipant.profile.lastName}`
                                                                : 'Unknown User'
                                                        }
                                                    </h2>
                                                    {selectedConversation.metadata?.gigTitle && (
                                                        <p className="text-xs sm:text-xs text-primary-600 dark:text-primary-400 truncate">
                                                            Re: {selectedConversation.metadata.gigTitle}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="relative flex-shrink-0" ref={kebabMenuRef}>
                                                <button
                                                    onClick={handleKebabMenuToggle}
                                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                                >
                                                    <FiMoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
                                                </button>

                                                {showKebabMenu && (
                                                    <div className="absolute right-0 mt-2 w-44 sm:w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                                                        <div className="py-1">
                                                            <button
                                                                onClick={handleArchiveConversation}
                                                                className="w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                            >
                                                                <FiArchive className="w-4 h-4 flex-shrink-0" />
                                                                <span>Archive</span>
                                                            </button>
                                                            <button
                                                                onClick={handleMuteConversation}
                                                                className="w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                            >
                                                                <FiVolumeX className="w-4 h-4 flex-shrink-0" />
                                                                <span>Mute</span>
                                                            </button>
                                                            <button
                                                                onClick={handleReportConversation}
                                                                className="w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                                            >
                                                                <FiFlag className="w-4 h-4 flex-shrink-0" />
                                                                <span>Report</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Messages */}
                                    <div className="flex-1 overflow-y-auto p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4" ref={messagesContainerRef}>
                                        {messages.map((message) => {
                                            const senderId = typeof message.sender === 'string' ? message.sender : message.sender?._id
                                            const isOwn = senderId === user?._id

                                            return (
                                                <div
                                                    key={message._id}
                                                    className={`flex group ${isOwn ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    <div className={`relative ${isMobile ? 'max-w-[280px]' : 'max-w-xs lg:max-w-md'
                                                        } px-3 sm:px-4 py-2 rounded-lg ${isOwn
                                                            ? 'bg-primary-600 text-white'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                                        }`}>
                                                        <p className="text-xs sm:text-sm md:text-base break-words">{message.content}</p>
                                                        <div className={`flex items-center justify-between gap-2 mt-1 text-xs ${isOwn ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'
                                                            }`}>
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs">{formatTimeAgo(message.createdAt)}</span>
                                                                {isOwn && (
                                                                    message.read ? (
                                                                        <FiCheckCircle className="w-3 h-3 flex-shrink-0" />
                                                                    ) : (
                                                                        <FiCheck className="w-3 h-3 flex-shrink-0" />
                                                                    )
                                                                )}
                                                            </div>
                                                            {isOwn && (
                                                                <button
                                                                    onClick={() => handleDeleteMessage(message._id)}
                                                                    disabled={deletingMessageId === message._id}
                                                                    className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black hover:bg-opacity-20 disabled:opacity-50 ${isOwn ? 'text-primary-100 hover:text-white' : 'text-gray-400 hover:text-gray-600'
                                                                        }`}
                                                                    title="Delete message"
                                                                >
                                                                    <FiTrash2 className="w-3 h-3 flex-shrink-0" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}

                                        {/* Typing Indicator */}
                                        {typingUsers.length > 0 && (
                                            <div className="flex justify-start">
                                                <div className="bg-gray-100 dark:bg-gray-700 px-3 sm:px-4 py-2 rounded-lg max-w-xs">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex space-x-1">
                                                            <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                            <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                            <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                        </div>
                                                        <span className="text-xs sm:text-xs text-gray-500 dark:text-gray-400">typing...</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Auto-scroll reference */}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {/* Message Input */}
                                    {(selectedOtherParticipant as any)?.isDeleted ? (
                                        <div className="p-4 sm:p-5 md:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                                            <div className="text-center">
                                                <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                                                    This account has been deleted. You cannot send messages.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-4 sm:p-5 md:p-6 border-t border-gray-200 dark:border-gray-700">
                                            {!isConnected && (
                                                <div className="mb-3 px-3 sm:px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                                    <p className="text-xs sm:text-sm md:text-base text-yellow-700 dark:text-yellow-300">
                                                        Connection lost. Messages may not be delivered in real-time.
                                                    </p>
                                                </div>
                                            )}
                                            <form onSubmit={handleSendMessage} className="flex items-center gap-2 sm:gap-3 w-full">
                                                <input
                                                    type="text"
                                                    value={newMessage}
                                                    onChange={(e) => handleTyping(e.target.value)}
                                                    className={`flex-1 min-w-0 px-4 ${isMobile ? 'py-3 text-base' : 'py-2 sm:py-2.5 text-sm'
                                                        } border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                                                    placeholder="Type your message..."
                                                    disabled={!isConnected}
                                                />

                                                <button
                                                    type="submit"
                                                    disabled={!newMessage.trim() || sending}
                                                    className={`flex-shrink-0 ${isMobile ? 'p-3' : 'p-2.5'
                                                        } bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                                                >
                                                    <FiSend className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4 sm:w-5 sm:h-5'}`} />
                                                </button>
                                            </form>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex-1 flex items-center justify-center p-4 sm:p-5 md:p-6">
                                    <div className="text-center">
                                        <FiMessageSquare className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-gray-300 dark:text-gray-600 mx-auto mb-3 sm:mb-4" />
                                        <h3 className="text-base sm:text-lg md:text-xl font-medium text-gray-900 dark:text-white mb-2">
                                            Select a conversation
                                        </h3>
                                        <p className="text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-400">
                                            Choose a conversation from the sidebar to start messaging
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default MessagesPage